import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import jwtDecode from 'jwt-decode';
import moment from 'moment';
import TagManager from 'react-gtm-module';
import { AppDispatch } from '../../../../app/App.store';
import { AchsJWT } from '../Types/AchsJWTInterface';
import {
    logout,
    renewJWTThunk,
    userIsAuthenticated,
    useSessionSelector
} from './sessionSlice';
import { getSessionIsTaken } from '@ui-core/utils/getSessionIsTaken.lib';
import { RenewJWTThunkBodyType } from '@ui-core/Types/RenewJWTThunkBodyType';
import { sendTagLog } from '@ui-core/utils/sendTagLog.lib';

const trackingTokenWithGTM = (token: string, azureObjectId?: string): void => {
    const dataLayer = {
        event: 'gaEvent',
        gaCategory: 'Home',
        gaAction: 'TrackingLogout',
        gaLabel: token,
        gaResponse: undefined,
        gaValue: '',
        gaNonInteraction: false,
        id: azureObjectId,
        timeStamp: moment().format(import.meta.env.REACT_APP_GTM_MOMENT_FORMAT)
    };

    TagManager.dataLayer({
        dataLayer,
        dataLayerName: 'PageDataLayer'
    });

    sendTagLog(dataLayer);
};

const execLogOut = (dispatch: AppDispatch): void => {
    setTimeout(() => {
        dispatch(logout());
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = `${import.meta.env.REACT_APP_HOST_BASE}/achs`;
    }, 250);
};

const getRenewJWTThunkBody = (sessionIsTaken?: any): RenewJWTThunkBodyType => {
    const id = localStorage.getItem('id') as string;
    let body: RenewJWTThunkBodyType;
    if (!sessionIsTaken) {
        body = { id };
    } else {
        body = {
            id,
            originalAuthorizationToken: sessionIsTaken.Authorization,
            renewOriginalSession: false
        };
    }

    return body;
};

interface ExecRenewJWTThunkInterface {
    timer: number;
    tokenString: string;
    tokenPayloadDecode: AchsJWT;
    dispatch: AppDispatch;
    isAuthenticated: boolean;
    body: RenewJWTThunkBodyType;
    remaining: number;
    gap: number;
}

const execRenewJWTThunk = async (values: ExecRenewJWTThunkInterface) => {
    const {
        timer,
        tokenString,
        tokenPayloadDecode,
        dispatch,
        isAuthenticated,
        body,
        remaining,
        gap
    } = values;

    switch (true) {
        case timer <= 0:
            // Token expires and exit app.
            await trackingTokenWithGTM(
                tokenString,
                tokenPayloadDecode.azureObjectId
            );
            execLogOut(dispatch);

            break;
        case timer > 0 &&
            timer <= Number(import.meta.env.REACT_APP_SESSION_EXPIRATION_TIME):
            if (!isAuthenticated) dispatch(userIsAuthenticated());

            dispatch(renewJWTThunk(body));
            break;
        case timer > Number(import.meta.env.REACT_APP_SESSION_EXPIRATION_TIME):
            if (!isAuthenticated) dispatch(userIsAuthenticated());

            setTimeout(() => {
                dispatch(renewJWTThunk(body));
            }, (remaining + gap) * 1000);
            break;
        default:
            break;
    }
};

interface ValidateTokenDataInterface {
    tokenIsValid: boolean;
    tokenPayloadDecode: AchsJWT;
    dispatch: AppDispatch;
    isAuthenticated: boolean;
    tokenString: string;
    sessionIsTaken: any;
}

type ValidateOriginalTokenDataType = Omit<
    ValidateTokenDataInterface,
    'tokenIsValid' | 'tokenPayloadDecode' | 'tokenString'
>;

const validateOriginalToken = (
    validateOriginalTokenData: ValidateOriginalTokenDataType
): void => {
    const { dispatch, isAuthenticated, sessionIsTaken } =
        validateOriginalTokenData;

    const token = sessionIsTaken.Authorization;
    const tokenPayloadDecode: AchsJWT = jwtDecode(token);
    const tokenString = token.split(' ')[1];

    const body: RenewJWTThunkBodyType = {
        id: sessionIsTaken.id,
        originalAuthorizationToken: token,
        renewOriginalSession: true
    };

    const currentDate = moment().unix();
    const expirationTokenDate = +sessionIsTaken.expirationTokenDate;
    const timer = expirationTokenDate - currentDate;
    const remaining =
        timer - Number(import.meta.env.REACT_APP_SESSION_EXPIRATION_TIME);
    const gap = 30;

    execRenewJWTThunk({
        timer,
        tokenString,
        tokenPayloadDecode,
        dispatch,
        isAuthenticated,
        body,
        remaining,
        gap
    });
};

const validateToken = async (
    validateTokenData: ValidateTokenDataInterface
): Promise<void> => {
    const {
        tokenIsValid,
        tokenPayloadDecode,
        dispatch,
        isAuthenticated,
        tokenString,
        sessionIsTaken
    } = validateTokenData;

    if (tokenIsValid) {
        const body = getRenewJWTThunkBody(sessionIsTaken);
        const expirationTokenDateString = localStorage.getItem(
            'expirationTokenDate'
        );
        let timer = 0;

        if (expirationTokenDateString) {
            const currentDate = moment().unix();
            const expirationTokenDate = Number.parseInt(
                expirationTokenDateString
            );

            timer = expirationTokenDate - currentDate;
        }

        const remaining =
            timer - Number(import.meta.env.REACT_APP_SESSION_EXPIRATION_TIME);
        const gap = 30;

        execRenewJWTThunk({
            timer,
            tokenString,
            tokenPayloadDecode,
            dispatch,
            isAuthenticated,
            body,
            remaining,
            gap
        });
    } else {
        await trackingTokenWithGTM(tokenString);
        execLogOut(dispatch);
    }
};

const Session: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const dispatch: AppDispatch = useDispatch();
    const { updatedAt, is429Error, isAuthenticated } = useSessionSelector();

    useEffect(() => {
        let tokenPayloadDecode = {} as AchsJWT;
        let tokenIsValid = false;
        const token = localStorage.getItem('Authorization') as string;
        let tokenString = '';
        try {
            tokenString = token.split(' ')[1];
            tokenPayloadDecode = jwtDecode(token);
            tokenIsValid = true;
        } catch (error: any) {
            // eslint-disable-next-line unicorn/no-console-spaces, no-console
            console.log('Token decode error: ', error.message);
        }

        const sessionIsTaken = getSessionIsTaken();

        if (sessionIsTaken) {
            validateOriginalToken({
                dispatch,
                isAuthenticated,
                sessionIsTaken
            });
        }

        validateToken({
            tokenIsValid,
            tokenPayloadDecode,
            dispatch,
            isAuthenticated,
            tokenString,
            sessionIsTaken
        });
    }, [dispatch, updatedAt, isAuthenticated]);

    useEffect(() => {
        const sessionIsTaken = getSessionIsTaken();
        const body = getRenewJWTThunkBody(sessionIsTaken);
        if (is429Error) {
            setTimeout(() => {
                if (sessionIsTaken) {
                    const originalBody: RenewJWTThunkBodyType = {
                        id: sessionIsTaken.id,
                        originalAuthorizationToken:
                            sessionIsTaken.Authorization,
                        renewOriginalSession: true
                    };

                    dispatch(renewJWTThunk(originalBody));
                }

                dispatch(renewJWTThunk(body));
            }, Number(import.meta.env.TIME_TO_RE_SEND_PETITION_DUE_TO_429));
        }
    }, [is429Error, dispatch]);

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
};

export default Session;
